import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { FaCalendar, FaEye, FaMapMarkerAlt, FaTag, FaArrowLeft, FaVolumeUp, FaMagic } from 'react-icons/fa';
import DOMPurify from 'dompurify';
import SEO from './SEO';
import BASE_URL, { SEO_BASE_URL } from '../config';
import { getBlogArticle } from '../api/client';
import { trackPageView } from '../utils/tracker';
import { ReadAloudButton } from './voice/TextToSpeech';
import { useVoiceSupport } from './voice/VoiceProvider';
import BlogSubscribeForm from './BlogSubscribeForm';
import BookingBlock from './BookingBlock';
import TripCostEstimator from './tools/TripCostEstimator';
import RelatedArticles from './RelatedArticles';
import { setPendingTripContext } from '../utils/tripContext';
import { trackEvent } from '../api/client';

/**
 * Simple markdown-to-HTML converter for blog content.
 * Handles headings, bold, italic, links, lists, images, blockquotes, and paragraphs.
 */
function renderMarkdown(md) {
  if (!md) return '';

  let html = md
    // Code blocks (``` ... ```)
    .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre class="bg-neutral-50 rounded-lg p-4 my-6 overflow-x-auto"><code class="text-sm text-neutral-800">$2</code></pre>')
    // Images ![alt](url)
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<figure class="my-8"><img src="$2" alt="$1" loading="lazy" class="rounded-xl w-full shadow-sm" /><figcaption class="text-center text-sm text-neutral-500 mt-3 italic">$1</figcaption></figure>')
    // Links [text](url)
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary-600 underline decoration-primary-300 underline-offset-2 hover:text-primary-700 hover:decoration-primary-500 transition-colors">$1</a>')
    // Headings - elegant serif styling with better spacing
    .replace(/^#### (.+)$/gm, '<h4 class="font-serif text-lg font-semibold text-neutral-800 mt-10 mb-4 tracking-tight">$1</h4>')
    .replace(/^### (.+)$/gm, '<h3 class="font-serif text-xl md:text-2xl font-bold text-neutral-800 mt-12 mb-5 tracking-tight leading-snug">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 class="font-serif text-2xl md:text-[1.75rem] font-bold text-neutral-900 mt-14 mb-6 leading-snug tracking-tight border-b border-neutral-100 pb-3">$1</h2>')
    // Bold and italic
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-neutral-900">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em class="italic">$1</em>')
    // Blockquotes - elegant pull quote styling
    .replace(/^> (.+)$/gm, '<blockquote class="border-l-4 border-primary-400 pl-6 py-2 my-8 text-lg text-neutral-600 italic font-serif bg-gradient-to-r from-primary-50/50 to-transparent rounded-r-lg">$1</blockquote>')
    // Horizontal rules
    .replace(/^---$/gm, '<hr class="my-10 border-0 h-px bg-gradient-to-r from-transparent via-neutral-300 to-transparent" />')
    // Unordered lists
    .replace(/^[*-] (.+)$/gm, '<li class="ml-6 mb-2 text-neutral-700">$1</li>')
    // Ordered lists
    .replace(/^\d+\. (.+)$/gm, '<li class="ml-6 mb-2 list-decimal text-neutral-700">$1</li>');

  // Wrap consecutive <li> in <ul>
  html = html.replace(/((?:<li[^>]*>.*<\/li>\n?)+)/g, '<ul class="list-disc my-6 space-y-1">$1</ul>');

  // Paragraphs: wrap lines that aren't already HTML tags
  html = html
    .split('\n\n')
    .map(block => {
      const trimmed = block.trim();
      if (!trimmed) return '';
      if (/^<[a-z]/.test(trimmed)) return trimmed;
      return `<p class="mb-7 text-neutral-700 leading-[1.85] text-[1.125rem] tracking-[-0.01em]">${trimmed}</p>`;
    })
    .join('\n');

  return html;
}

/**
 * Cut the rendered body into three fragments so components can be placed
 * *inside* the article rather than after it.
 *
 * The body arrives as one opaque HTML string, so there is no JSX seam to insert
 * into. Splitting is done on the parsed DOM rather than by string
 * matching on "</p>", so a paragraph nested inside a table or figure can never
 * cause a split mid-element. Returns null for short articles or if anything
 * goes wrong — the caller then renders the body whole, exactly as before.
 */
function splitArticleBody(html) {
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return null;
  let nodes;
  try {
    const doc = new window.DOMParser().parseFromString(html, 'text/html');
    nodes = Array.from(doc.body.childNodes);
  } catch {
    return null;
  }

  const serialize = (list) =>
    list.map((n) => (n.outerHTML !== undefined ? n.outerHTML : n.textContent || '')).join('');

  const paragraphs = [];
  nodes.forEach((n, i) => {
    if (n.nodeName === 'P') paragraphs.push(i);
  });

  // Below six paragraphs there is no room for two insertions without the
  // article reading as a stack of promo boxes.
  if (paragraphs.length < 6) return null;

  const ctaAt = paragraphs[2] + 1; // after the third paragraph
  // Roughly two-thirds through, but never within two paragraphs of the CTA and
  // never so late that no article remains beneath it.
  const target = Math.round((paragraphs.length * 2) / 3) - 1;
  const bookingPara = Math.min(paragraphs.length - 2, Math.max(4, target));
  const bookingAt = paragraphs[bookingPara] + 1;

  return {
    lead: serialize(nodes.slice(0, ctaAt)),
    middle: serialize(nodes.slice(ctaAt, bookingAt)),
    tail: serialize(nodes.slice(bookingAt)),
  };
}

const ARTICLE_BODY_STYLE = {
  fontFeatureSettings: '"kern" 1, "liga" 1',
  textRendering: 'optimizeLegibility',
  WebkitFontSmoothing: 'antialiased',
};

/**
 * The first call to action, placed after roughly the third paragraph — where
 * attention is still high — rather than at the foot of the page. It carries the
 * article's destination straight into the planner.
 */
const InArticleCTA = ({ destination, onPlanTrip }) => (
  <div className="my-10 rounded-xl border-l-4 border-primary-500 bg-gradient-to-r from-primary-50 to-transparent px-5 py-4 flex flex-wrap items-center justify-between gap-4">
    <div className="min-w-[14rem] flex-1">
      <p className="font-semibold text-neutral-900 mb-0.5">
        {destination ? `Planning a trip to ${destination}?` : 'Planning this trip?'}
      </p>
      <p className="text-sm text-neutral-600 leading-relaxed">
        Get a free AI day-plan{destination ? ` for ${destination}` : ''} with real attractions,
        photos and maps — no signup needed.
      </p>
    </div>
    <button
      onClick={onPlanTrip}
      className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors whitespace-nowrap"
    >
      <FaMagic size={13} /> Plan this trip free
    </button>
  </div>
);

const BlogArticle = ({ slug, onBack, onViewBlog, onViewTryout, onViewArticle, onPlanTrip }) => {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const voiceSupport = useVoiceSupport();

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    getBlogArticle(slug)
      .then(data => setArticle(data))
      .catch(err => setError(err?.response?.status === 404 ? 'Article not found.' : 'Failed to load article.'))
      .finally(() => setLoading(false));
  }, [slug]);

  // Track page view when article loads successfully (ensures slug is always captured)
  useEffect(() => {
    if (article && slug) {
      trackPageView(`/blog/${slug}`);
    }
  }, [article, slug]);

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Generate plain text for TTS (must be before early returns to satisfy React hooks rules)
  const articleReadAloudText = useMemo(() => {
    if (!article) return '';
    // Convert markdown content to plain text
    let plainText = article.content || '';
    // Remove markdown formatting
    plainText = plainText
      .replace(/```[\s\S]*?```/g, '') // Remove code blocks
      .replace(/!\[([^\]]*)\]\([^)]+\)/g, '') // Remove images
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Convert links to just text
      .replace(/^#{1,4}\s+(.+)$/gm, '$1.') // Convert headings to text with period
      .replace(/\*\*\*(.+?)\*\*\*/g, '$1') // Remove bold+italic
      .replace(/\*\*(.+?)\*\*/g, '$1') // Remove bold
      .replace(/\*(.+?)\*/g, '$1') // Remove italic
      .replace(/^>\s+(.+)$/gm, '$1') // Remove blockquotes
      .replace(/^[*-]\s+/gm, '') // Remove list markers
      .replace(/^\d+\.\s+/gm, '') // Remove numbered list markers
      .replace(/---/g, '') // Remove horizontal rules
      .replace(/\n{3,}/g, '\n\n') // Reduce multiple newlines
      .trim();

    // Prepend title
    const intro = `${article.title}. ${article.excerpt || ''}`;
    return `${intro} ${plainText}`;
  }, [article]);

  // Sanitize HTML, force links to open in a new tab, then cut the body into the
  // fragments the in-article CTA and booking block sit between.
  const body = useMemo(() => {
    if (!article) return { html: '', segments: null };
    let html = DOMPurify.sanitize(renderMarkdown(article.content));
    html = html.replace(
      /<a\s+(?![^>]*target=)([^>]*href="[^"]*"[^>]*)>/gi,
      '<a $1 target="_blank" rel="noopener noreferrer">'
    );
    return { html, segments: splitArticleBody(html) };
  }, [article]);

  // Carries the article's destination into the planner. Whatever dates the
  // reader picked in the booking block are already in the pending context, so
  // the preview opens on a trip they never had to type.
  const handlePlanTrip = useCallback(() => {
    const destination = article?.destination || '';
    setPendingTripContext({ destination, source: `blog_${slug}` });
    trackEvent('blog_plan_trip_clicked', { slug, destination });
    if (onPlanTrip) {
      onPlanTrip(destination);
    } else if (onViewTryout) {
      onViewTryout();
    } else {
      window.location.href = '/tryout';
    }
  }, [article, slug, onPlanTrip, onViewTryout]);

  if (loading) {
    return <div className="text-center py-20 text-neutral-400">Loading article...</div>;
  }

  if (error || !article) {
    return (
      <div className="text-center py-20">
        <p className="text-neutral-500 text-lg mb-4">{error || 'Article not found.'}</p>
        <button onClick={onViewBlog || onBack} className="text-primary-600 hover:underline">
          &larr; Back to Blog
        </button>
      </div>
    );
  }

  // Generate Article schema JSON-LD
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': article.seo_title || article.title,
    'description': article.seo_description || article.excerpt || '',
    'image': article.featured_image || `${BASE_URL}/og-image.png`,
    'author': article.author_name
      ? {
          '@type': 'Person',
          'name': article.author_name,
          'description': article.author_credentials || article.author_bio || undefined,
        }
      : {
          '@type': 'Organization',
          'name': 'ItinerarySeek',
          'url': BASE_URL,
        },
    'publisher': {
      '@type': 'Organization',
      'name': 'ItinerarySeek',
      'logo': {
        '@type': 'ImageObject',
        'url': `${BASE_URL}/logo192.png`,
      },
    },
    'url': `${BASE_URL}/blog/${article.slug}`,
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': `${BASE_URL}/blog/${article.slug}`,
    },
    'datePublished': article.published_at || article.created_at,
    'dateModified': article.updated_at || article.published_at || article.created_at,
    'articleSection': article.category || 'Travel',
    'keywords': Array.isArray(article.tags) ? article.tags.join(', ') : '',
  };

  // Add BreadcrumbList schema
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': 'Home',
        'item': BASE_URL,
      },
      {
        '@type': 'ListItem',
        'position': 2,
        'name': 'Blog',
        'item': `${BASE_URL}/blog`,
      },
      {
        '@type': 'ListItem',
        'position': 3,
        'name': article.title,
        'item': `${BASE_URL}/blog/${article.slug}`,
      },
    ],
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <SEO
        title={article.seo_title || article.title}
        description={article.seo_description || article.excerpt || ''}
        canonical={`${SEO_BASE_URL}/blog/${article.slug}`}
        type="article"
        image={article.featured_image}
      />

      {/* Article and Breadcrumb Schema JSON-LD */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      {/* Back link */}
      <button
        onClick={onViewBlog || onBack}
        className="flex items-center gap-2 text-neutral-600 hover:text-primary-600 mb-6 font-medium transition-colors"
      >
        <FaArrowLeft className="text-sm" /> Back to Blog
      </button>

      {/* Featured image */}
      {article.featured_image && (
        <div className="rounded-xl overflow-hidden mb-8">
          <img
            src={article.featured_image}
            alt={article.title}
            className="w-full max-h-[400px] object-cover"
          />
        </div>
      )}

      {/* Article header */}
      <header className="mb-8">
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <span className="text-sm font-semibold text-primary-600 bg-primary-50 px-3 py-1 rounded-full">
            {article.category}
          </span>
          {article.destination && (
            <span className="text-sm text-neutral-500 flex items-center gap-1">
              <FaMapMarkerAlt className="text-xs" /> {article.destination}
            </span>
          )}
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4 leading-tight">
          {article.title}
        </h1>
        {article.excerpt && (
          <p className="text-lg text-neutral-600 mb-4">{article.excerpt}</p>
        )}
        <div className="flex items-center gap-4 text-sm text-neutral-400 flex-wrap">
          {article.author_name && (
            <span className="text-neutral-600">
              By <strong className="text-neutral-800">{article.author_name}</strong>
              {article.author_credentials ? `, ${article.author_credentials}` : ''}
            </span>
          )}
          <span className="flex items-center gap-1"><FaCalendar /> {formatDate(article.published_at)}</span>
          <span className="flex items-center gap-1"><FaEye /> {article.view_count} views</span>
          {voiceSupport.speechSynthesis && (
            <ReadAloudButton
              text={articleReadAloudText}
              buttonText="Listen to Article"
              speakingText="Stop Listening"
              className="text-xs"
            />
          )}
        </div>
      </header>

      {/* Article body - elegant typography for comfortable reading.
          The body is split so the CTA lands after the third paragraph and the
          booking block roughly two-thirds through, where the reader is still
          engaged; a body too short to split renders whole, as before. */}
      {body.segments ? (
        <>
          <article
            className="prose prose-neutral max-w-none article-content article-content--lead"
            style={ARTICLE_BODY_STYLE}
            dangerouslySetInnerHTML={{ __html: body.segments.lead }}
          />

          <InArticleCTA destination={article.destination} onPlanTrip={handlePlanTrip} />

          <article
            className="prose prose-neutral max-w-none article-content"
            style={ARTICLE_BODY_STYLE}
            dangerouslySetInnerHTML={{ __html: body.segments.middle }}
          />

          {article.destination && (
            <BookingBlock destination={article.destination} surface={`blog_${slug}`} />
          )}

          <article
            className="prose prose-neutral max-w-none article-content"
            style={ARTICLE_BODY_STYLE}
            dangerouslySetInnerHTML={{ __html: body.segments.tail }}
          />
        </>
      ) : (
        <>
          <article
            className="prose prose-neutral max-w-none article-content article-content--lead"
            style={ARTICLE_BODY_STYLE}
            dangerouslySetInnerHTML={{ __html: body.html }}
          />

          <InArticleCTA destination={article.destination} onPlanTrip={handlePlanTrip} />

          {article.destination && (
            <BookingBlock destination={article.destination} surface={`blog_${slug}`} />
          )}
        </>
      )}
      <style>{`
        .article-content--lead > p:first-of-type::first-letter {
          font-size: 3.5rem;
          font-weight: 700;
          font-family: Georgia, serif;
          float: left;
          line-height: 1;
          margin-right: 0.5rem;
          margin-top: 0.1rem;
          color: #1a1a1a;
        }
        .article-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 2rem 0;
          font-size: 0.95rem;
        }
        .article-content th, .article-content td {
          padding: 0.75rem 1rem;
          text-align: left;
          border-bottom: 1px solid #e5e5e5;
        }
        .article-content th {
          background: #f9fafb;
          font-weight: 600;
          color: #374151;
        }
        .article-content tr:hover td {
          background: #fafafa;
        }
      `}</style>

      {/* Subscribe form — seeded with this article's destination, so the drip
          is about the place they were reading about. */}
      <BlogSubscribeForm compact={true} destination={article.destination} />

      {/* Author bio footer */}
      {article.author_name && article.author_bio && (
        <div className="mt-10 pt-6 border-t border-neutral-200">
          <h3 className="text-sm font-semibold text-neutral-800 mb-1">About the author</h3>
          <p className="text-sm text-neutral-600">
            <strong>{article.author_name}</strong>
            {article.author_credentials ? `, ${article.author_credentials}` : ''} — {article.author_bio}
          </p>
        </div>
      )}

      {/* Tags */}
      {article.tags && article.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-10 pt-6 border-t border-neutral-200">
          {article.tags.map(tag => (
            <span key={tag} className="text-sm text-neutral-500 bg-neutral-100 px-3 py-1 rounded-full flex items-center gap-1">
              <FaTag className="text-xs" /> {tag}
            </span>
          ))}
        </div>
      )}

      {/* Somewhere to go next — same destination first, then same category */}
      {/* "What will this cost?" is the question a reader has by the end of a
          destination piece, and answering it here keeps them on the page. The
          estimate is generated only when they ask for it. */}
      {article.destination && (
        <TripCostEstimator
          initialDestination={article.destination}
          compact
          surface={`blog_${slug}_cost`}
          onPlanTrip={handlePlanTrip}
        />
      )}

      {/* The closing bridge. 9.1 asks every article to end with a button that
          carries its destination straight into the planner: a reader who got
          this far is the most engaged reader the article will ever have, and
          the gap between "this piece is about Kyoto" and "here is your Kyoto
          plan" should be one click and no typing. */}
      {article.destination && (
        <div className="my-10 rounded-xl border border-primary-200 bg-gradient-to-r from-primary-50 to-transparent px-6 py-6 text-center">
          <h2 className="font-serif text-xl font-bold text-neutral-900">
            Ready to plan {article.destination}?
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-neutral-600">
            We'll turn everything you've just read into a day-by-day plan — the stops,
            the timings and where to stay. Free, and there's nothing to fill in.
          </p>
          <button
            onClick={handlePlanTrip}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700"
          >
            <FaMagic size={13} /> Plan my {article.destination} trip free
          </button>
        </div>
      )}

      <RelatedArticles
        slug={slug}
        destination={article.destination}
        category={article.category}
        onViewArticle={onViewArticle}
      />

      {/* CTA */}
      <div className="mt-10 p-6 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-xl text-center">
        <h3 className="text-xl font-bold text-neutral-800 mb-2">
          {article.destination ? `Ready to plan your ${article.destination} trip?` : 'Ready to plan your trip?'}
        </h3>
        <p className="text-neutral-600 mb-4">
          {article.destination
            ? `Start with a free AI day-plan for ${article.destination}, then turn it into a full itinerary.`
            : 'Create a personalized AI-powered itinerary in seconds.'}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={handlePlanTrip}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors"
          >
            <FaMagic size={14} />
            {article.destination ? `Plan ${article.destination} free` : 'Plan my trip free'}
          </button>
          <a href="/register" className="text-sm font-medium text-neutral-600 hover:text-primary-600">
            or create a free account
          </a>
        </div>
      </div>

      {/* Editorial transparency notice - at the bottom */}
      {article.ai_generated && (
        <div className="mt-10 pt-6 border-t border-neutral-200 text-xs text-neutral-500 bg-neutral-50 border border-neutral-200 rounded-md px-3 py-2">
          <strong>Editorial transparency:</strong> This article was drafted with
          the assistance of generative AI and
          {article.reviewed_by
            ? <> reviewed and edited by <strong>{article.reviewed_by}</strong> before publication.</>
            : <> reviewed by a human editor before publication.</>}
          {' '}We verify key facts, cite sources where relevant, and welcome corrections at
          {' '}<a href="/contact" className="text-primary-600 underline">our contact page</a>.
        </div>
      )}
    </div>
  );
};

export default BlogArticle;
